<script setup lang="ts">
import { computed } from 'vue'
import {
  LISTING_TYPE_OPTIONS,
  LISTING_PROCESSING_STATUS_OPTIONS,
  POSTER_TYPE,
} from '@/modules/realestate/constants/realestate.const'

interface Props {
  editable: boolean
  form: any
  listing?: any
  newData?: any
  approveForm?: boolean
}

const props = defineProps<Props>()

const editable = computed(() => props.editable)

/** Format ISO datetime to Vietnamese locale string */
const formatDate = (iso: string | null | undefined): string => {
  if (!iso) return '--'
  try {
    return new Date(iso).toLocaleString('vi-VN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return iso
  }
}

const posterTypeLabel = computed(() => {
  const pt = props.listing?.poster_type
  if (pt === POSTER_TYPE.USER) return 'Cá nhân'
  if (pt === POSTER_TYPE.COMPANY) return 'Môi giới / Công ty'
  return '--'
})
</script>

<template>
  <div class="flex flex-col w-full pt-3 gap-y-5">
    <InputText type="text" hidden name="poster_type" />
    <div class="grid md:grid-cols-2 w-full md:flex-3">
      <!-- Mã tin -->
      <div class="flex mb-3 gap-3">
        <div class="flex-1 text-right">Mã tin:</div>
        <div class="flex-2">
          <InputText name="code" type="text" class="form-control" disabled />
        </div>
      </div>

      <!-- Loại tin -->
      <div class="flex mb-3 gap-3">
        <div class="flex-1 text-right">Loại tin:</div>
        <div class="flex-2">
          <Select
            name="listing_type"
            :options="LISTING_TYPE_OPTIONS.map((item: any) => ({ name: item.label, code: item.value }))"
            optionLabel="name"
            placeholder="Chọn loại tin"
            :class="`form-control ${
              props.newData && props.form?.fields?.listing_type?.states?.value?.code !== props.newData?.listing_type
                ? '!bg-red-100 !box-border !px-2 !py-1'
                : ''
            }`"
            :disabled="!editable"
          />
          <span class="font-italic text-[12px] text-red-500">
            {{ props.form?.fields?.listing_type?.states?.error?.message }}
          </span>
          <template
            v-if="
              props.newData &&
              props.form?.fields?.listing_type?.states?.value?.code !== props.newData?.listing_type
            "
          >
            <InputText
              :value="LISTING_TYPE_OPTIONS.find((item) => item.value === props.newData?.listing_type)?.label"
              disabled
              class="form-control !bg-green-100 !text-green-800 !px-2 !py-1"
            />
          </template>
        </div>
      </div>

      <!-- Trạng thái xử lý -->
      <div class="flex mb-3 gap-3">
        <div class="flex-1 text-right">Trạng thái:</div>
        <div class="flex-2">
          <Select
            name="processing_status"
            :options="LISTING_PROCESSING_STATUS_OPTIONS.map((item: any) => ({ name: item.label, code: item.value }))"
            optionLabel="name"
            placeholder="Chọn trạng thái"
            class="form-control"
            :disabled="props.approveForm || !editable"
          />
          <span class="font-italic text-[12px] text-red-500">
            {{ props.form?.fields?.processing_status?.states?.error?.message }}
          </span>
        </div>
      </div>

      <!-- Người đăng -->
      <div class="flex mb-3 gap-3">
        <div class="flex-1 text-right">Người đăng:</div>
        <div class="flex-2">
          <InputText name="poster_name" type="text" class="form-control" disabled />
        </div>
      </div>

      <!-- Loại người đăng (poster_type) — read-only display -->
      <div class="flex mb-3 gap-3" v-if="props.listing?.poster_type">
        <div class="flex-1 text-right">Vai trò người đăng:</div>
        <div class="flex-2">
          <span class="font-semibold text-sm">{{ posterTypeLabel }}</span>
        </div>
      </div>

      <!-- Ngày tạo -->
      <div class="flex mb-3 gap-3" v-if="props.listing?.created_at">
        <div class="flex-1 text-right">Ngày tạo:</div>
        <div class="flex-2">
          <span class="text-sm text-gray-600">{{ formatDate(props.listing.created_at) }}</span>
        </div>
      </div>

      <!-- Ngày cập nhật -->
      <div class="flex mb-3 gap-3" v-if="props.listing?.updated_at">
        <div class="flex-1 text-right">Cập nhật lần cuối:</div>
        <div class="flex-2">
          <span class="text-sm text-gray-600">{{ formatDate(props.listing.updated_at) }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped></style>
